import { waveGraphQLFetch } from "./httpClient.js";
import { WAVE_GRAPHQL } from "./queries.js";
import { parseWaveChatAuthorKind, parseWaveChatMessageStatus, } from "../waveChat.js";
import { parseWaveRole, parseWaveStatus, WaveStatus, } from "../types.js";
async function gql(cfg, query, variables, key) {
    const token = await cfg.getIdToken();
    return waveGraphQLFetch({
        endpoint: cfg.endpoint,
        idToken: token,
        query,
        variables,
        resultKey: key,
    });
}
function isTodoCreateConflict(err) {
    const msg = err instanceof Error ? err.message : String(err);
    return /conditional/i.test(msg);
}
function parseWaveSpaceRole(raw) {
    return raw === "OWNER" || raw === "EDITOR" || raw === "VIEWER" ? raw : "VIEWER";
}
function mapOceanSpace(dto) {
    return {
        spaceId: dto.spaceId,
        title: dto.title,
        icon: dto.icon ?? null,
        color: dto.color ?? null,
        visibility: dto.visibility === "ORG" ? "ORG" : "PRIVATE",
        myRole: parseWaveSpaceRole(dto.myRole),
        memberCount: dto.memberCount ?? 1,
        shareable: dto.shareable ?? true,
        reserved: dto.reserved ?? null,
        ownerSub: dto.ownerSub ?? null,
        createdAt: dto.createdAt,
        updatedAt: dto.updatedAt,
    };
}
function mapOceanSpaceMember(dto) {
    return {
        spaceId: dto.spaceId,
        sub: dto.sub,
        email: dto.email ?? null,
        role: parseWaveSpaceRole(dto.role),
        addedAt: dto.addedAt,
    };
}
function mapWaveItem(item) {
    return {
        waveId: item.waveId,
        spaceId: item.spaceId,
        title: item.title ?? "Wave",
        status: parseWaveStatus(item.status),
        templateId: item.templateId,
        formatVersion: item.formatVersion ?? "block-v1",
        ownerSub: item.ownerSub,
        participants: [],
        latestSnapshotSeq: item.latestSnapshotSeq ?? 0,
        latestSnapshotB64: item.latestSnapshotB64,
        nextOpSeq: item.nextOpSeq ?? 0,
        coverImageS3Key: item.coverImageS3Key ?? null,
        coverImageHeaderS3Key: item.coverImageHeaderS3Key ?? null,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
    };
}
function mapWave(dto) {
    const participants = (dto.participants ?? []).map((p) => ({
        sub: p.sub,
        email: p.email,
        displayName: p.displayName,
        role: parseWaveRole(p.role),
        joinedAt: p.joinedAt,
    }));
    return {
        waveId: dto.waveId,
        spaceId: dto.spaceId,
        title: dto.title ?? "Wave",
        status: parseWaveStatus(dto.status),
        templateId: dto.templateId,
        formatVersion: dto.formatVersion ?? "block-v1",
        ownerSub: dto.ownerSub,
        participants,
        latestSnapshotSeq: dto.latestSnapshotSeq ?? 0,
        latestSnapshotB64: dto.latestSnapshotB64,
        nextOpSeq: dto.nextOpSeq ?? 0,
        coverImageS3Key: dto.coverImageS3Key ?? null,
        coverImageHeaderS3Key: dto.coverImageHeaderS3Key ?? null,
        createdAt: dto.createdAt,
        updatedAt: dto.updatedAt,
    };
}
export const WaveCloudAPI = {
    /** Tutti i miei spazi OceanSpace (cross-app), sorgente unica di grouping web. */
    async listOceanSpaces(cfg) {
        const rows = await gql(cfg, WAVE_GRAPHQL.listOceanSpaces, {}, "listOceanSpaces");
        return (rows ?? []).map(mapOceanSpace);
    },
    async getOceanSpace(cfg, spaceId) {
        const row = await gql(cfg, WAVE_GRAPHQL.getOceanSpace, { id: spaceId }, "getOceanSpace");
        return row ? mapOceanSpace(row) : null;
    },
    async createOceanSpace(cfg, title) {
        const row = await gql(cfg, WAVE_GRAPHQL.createOceanSpace, { i: { title } }, "createOceanSpace");
        return mapOceanSpace(row);
    },
    async updateOceanSpace(cfg, spaceId, patch) {
        const row = await gql(cfg, WAVE_GRAPHQL.updateOceanSpace, {
            i: {
                spaceId,
                title: patch.title ?? undefined,
                icon: patch.icon ?? undefined,
                color: patch.color ?? undefined,
            },
        }, "updateOceanSpace");
        return mapOceanSpace(row);
    },
    async deleteOceanSpace(cfg, spaceId) {
        await gql(cfg, WAVE_GRAPHQL.deleteOceanSpace, { id: spaceId }, "deleteOceanSpace");
    },
    async listSpaceMembers(cfg, spaceId) {
        const rows = await gql(cfg, WAVE_GRAPHQL.listOceanSpaceMembers, { id: spaceId }, "listOceanSpaceMembers");
        return (rows ?? []).map(mapOceanSpaceMember);
    },
    async addSpaceMember(cfg, spaceId, email, role) {
        const normalized = email.trim().toLowerCase();
        if (!normalized.includes("@")) {
            throw new Error("Inserisci un indirizzo email valido.");
        }
        const row = await gql(cfg, WAVE_GRAPHQL.addSpaceMember, { id: spaceId, email: normalized, role }, "addSpaceMember");
        return mapOceanSpaceMember(row);
    },
    async updateSpaceMemberRole(cfg, spaceId, sub, role) {
        const row = await gql(cfg, WAVE_GRAPHQL.updateSpaceMemberRole, { id: spaceId, sub, role }, "updateSpaceMemberRole");
        return mapOceanSpaceMember(row);
    },
    async removeSpaceMember(cfg, spaceId, sub) {
        await gql(cfg, WAVE_GRAPHQL.removeSpaceMember, { id: spaceId, sub }, "removeSpaceMember");
    },
    async listWaves(cfg, spaceId) {
        const conn = await gql(cfg, WAVE_GRAPHQL.listWaves, { s: spaceId }, "listWaves");
        return conn.items.map(mapWaveItem);
    },
    async listParticipatingWaves(cfg) {
        let conn;
        try {
            conn = await gql(cfg, WAVE_GRAPHQL.listParticipatingWaves, { l: 50 }, "listParticipatingWaves");
        }
        catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            if (msg.includes("FieldUndefined") || msg.includes("listParticipatingWaves")) {
                return [];
            }
            throw err;
        }
        const waves = [];
        for (const ref of conn.items) {
            try {
                waves.push(await WaveCloudAPI.getWave(cfg, ref.waveId));
            }
            catch {
                const now = new Date().toISOString();
                waves.push({
                    waveId: ref.waveId,
                    spaceId: "",
                    title: "Wave condivisa",
                    status: WaveStatus.Draft,
                    ownerSub: "",
                    participants: [],
                    latestSnapshotSeq: 0,
                    nextOpSeq: 0,
                    createdAt: now,
                    updatedAt: now,
                });
            }
        }
        return waves;
    },
    /** Owned waves across all spaces (summary rows from listWaves). */
    async listOwnedWaves(cfg, ownerSub) {
        const spaces = await WaveCloudAPI.listOceanSpaces(cfg);
        const result = [];
        const seen = new Set();
        for (const space of spaces) {
            const items = await WaveCloudAPI.listWaves(cfg, space.spaceId);
            for (const item of items) {
                if (seen.has(item.waveId))
                    continue;
                if (item.ownerSub !== ownerSub)
                    continue;
                seen.add(item.waveId);
                result.push(item);
            }
        }
        return result;
    },
    /** Owned waves with at least one other participant (shared by current user). */
    async listSharedByMeWaves(cfg, ownerSub) {
        const spaces = await WaveCloudAPI.listOceanSpaces(cfg);
        const result = [];
        const seen = new Set();
        for (const space of spaces) {
            const items = await WaveCloudAPI.listWaves(cfg, space.spaceId);
            for (const item of items) {
                if (seen.has(item.waveId))
                    continue;
                seen.add(item.waveId);
                try {
                    const full = await WaveCloudAPI.getWave(cfg, item.waveId);
                    const hasGuest = full.participants.some((p) => p.sub !== ownerSub);
                    if (full.ownerSub === ownerSub && hasGuest) {
                        result.push(full);
                    }
                }
                catch {
                    // skip inaccessible waves
                }
            }
        }
        return result;
    },
    async getWave(cfg, waveId) {
        const dto = await gql(cfg, WAVE_GRAPHQL.getWave, { id: waveId }, "getWave");
        return mapWave(dto);
    },
    async createWave(cfg, spaceId, title, templateId, status, formatVersion) {
        const dto = await gql(cfg, WAVE_GRAPHQL.createWave, {
            i: {
                spaceId,
                title,
                templateId,
                status,
                formatVersion: formatVersion ?? undefined,
            },
        }, "createWave");
        return mapWave(dto);
    },
    async appendOps(cfg, waveId, opsB64) {
        return gql(cfg, WAVE_GRAPHQL.appendOps, { w: waveId, o: opsB64 }, "appendWaveOps");
    },
    async listOpsSince(cfg, waveId, afterSeq, nextToken) {
        return gql(cfg, WAVE_GRAPHQL.listOpsSince, { w: waveId, a: afterSeq, t: nextToken ?? null }, "listWaveOpsSince");
    },
    async getWaveYjsSync(cfg, waveId, stateVectorB64) {
        return gql(cfg, WAVE_GRAPHQL.getWaveYjsSync, { w: waveId, sv: stateVectorB64 ?? null }, "getWaveYjsSync");
    },
    async listAllOps(cfg, waveId, afterSeq = 0) {
        const all = [];
        let token = null;
        let pages = 0;
        // Paginate DynamoDB even when a page filters to zero ops (seq <= afterSeq).
        do {
            const conn = await WaveCloudAPI.listOpsSince(cfg, waveId, afterSeq, token);
            if (conn.items.length)
                all.push(...conn.items);
            token = conn.nextToken ?? null;
            pages += 1;
            // Guardia runaway: il poll incrementale deve risolversi in poche pagine;
            // oltre il cap meglio un tail parziale (reconcile colma) che bloccare il tick.
            if (token && pages >= 50) {
                if (typeof console !== "undefined") {
                    console.warn("[Wave Yjs] listAllOps page cap reached", `waveId=${waveId} afterSeq=${afterSeq} pages=${pages}`);
                }
                break;
            }
        } while (token);
        return all;
    },
    async publishSnapshot(cfg, waveId, snapshotB64, seq, formatVersion) {
        await gql(cfg, WAVE_GRAPHQL.publishSnapshot, {
            w: waveId,
            s: snapshotB64,
            n: seq,
            fv: formatVersion ?? undefined,
        }, "publishWaveSnapshot");
    },
    async updateStatus(cfg, waveId, status) {
        const dto = await gql(cfg, WAVE_GRAPHQL.updateStatus, { w: waveId, s: status }, "updateWaveStatus");
        return mapWaveItem(dto);
    },
    async updateWave(cfg, waveId, title) {
        const normalized = title.trim();
        if (!normalized)
            throw new Error("Il titolo non può essere vuoto.");
        const dto = await gql(cfg, WAVE_GRAPHQL.updateWave, { w: waveId, i: { title: normalized } }, "updateWave");
        return mapWaveItem(dto);
    },
    async postBlip(cfg, waveId, blockAnchorId, body) {
        return gql(cfg, WAVE_GRAPHQL.postBlip, { i: { waveId, blockAnchorId, body } }, "postWaveBlip");
    },
    async listBlips(cfg, waveId) {
        return gql(cfg, WAVE_GRAPHQL.listBlips, { w: waveId }, "listWaveBlips");
    },
    async listPresences(cfg, waveId) {
        return gql(cfg, WAVE_GRAPHQL.listPresences, { w: waveId }, "listWavePresences");
    },
    async listLastSeen(cfg, waveId) {
        return gql(cfg, WAVE_GRAPHQL.listLastSeen, { w: waveId }, "listWaveLastSeen");
    },
    async inviteParticipant(cfg, waveId, email, role) {
        const normalized = email.trim().toLowerCase();
        if (!normalized.includes("@")) {
            throw new Error("Inserisci un indirizzo email valido.");
        }
        const row = await gql(cfg, WAVE_GRAPHQL.inviteParticipant, { i: { waveId, email: normalized, role } }, "inviteWaveParticipant");
        return {
            sub: row.sub,
            email: row.email,
            role: parseWaveRole(row.role),
            joinedAt: row.joinedAt,
        };
    },
    async publishPresence(cfg, waveId, clientId, blockAnchorId, cursorOffset, cursorEndOffset = null) {
        return gql(cfg, WAVE_GRAPHQL.publishPresence, {
            i: {
                waveId,
                clientId,
                blockAnchorId,
                cursorOffset,
                cursorEndOffset,
            },
        }, "publishWavePresence");
    },
    async joinWaveViaLink(cfg, waveId) {
        const row = await gql(cfg, WAVE_GRAPHQL.joinWaveViaLink, { w: waveId }, "joinWaveViaLink");
        return {
            sub: row.sub,
            email: row.email,
            displayName: row.displayName,
            role: parseWaveRole(row.role),
            joinedAt: row.joinedAt,
        };
    },
    async getAIJob(cfg, jobId) {
        return gql(cfg, WAVE_GRAPHQL.getAIJob, { id: jobId }, "getAIJob");
    },
    async listOceanMissionSteps(cfg, jobId, limit = 50) {
        const row = await gql(cfg, WAVE_GRAPHQL.listOceanMissionSteps, { jobId, limit }, "listOceanMissionSteps");
        return row.items ?? [];
    },
    async listOceanAgents(cfg, limit = 50) {
        const row = await gql(cfg, WAVE_GRAPHQL.listOceanAgents, { limit }, "listOceanAgents");
        return row.items ?? [];
    },
    async requestWaveCover(cfg, waveId, force = false) {
        return gql(cfg, WAVE_GRAPHQL.requestWaveCoverImage, { id: waveId, force }, "requestWaveCoverImage");
    },
    async submitAIJob(cfg, input) {
        return gql(cfg, WAVE_GRAPHQL.submitAIJob, { input }, "submitAIJob");
    },
    async ensureProfile(cfg) {
        await gql(cfg, WAVE_GRAPHQL.upsertProfile, undefined, "upsertProfile");
    },
    async listChatMessages(cfg, waveId, limit = 50, nextToken) {
        const conn = await gql(cfg, WAVE_GRAPHQL.listChatMessages, { waveId, limit, nextToken }, "listWaveChatMessages");
        return {
            items: (conn.items ?? []).map(mapChatMessageDto),
            nextToken: conn.nextToken ?? null,
        };
    },
    async listChatReplies(cfg, waveId, parentId, limit = 50, nextToken) {
        const conn = await gql(cfg, WAVE_GRAPHQL.listChatReplies, { waveId, parentId, limit, nextToken }, "listWaveChatReplies");
        return {
            items: (conn.items ?? []).map(mapChatMessageDto),
            nextToken: conn.nextToken ?? null,
        };
    },
    async getChatReadState(cfg, waveId) {
        const row = await gql(cfg, WAVE_GRAPHQL.getChatReadState, { waveId }, "getWaveChatReadState");
        return row;
    },
    async postChatMessage(cfg, input) {
        const row = await gql(cfg, WAVE_GRAPHQL.postChatMessage, { input }, "postWaveChatMessage");
        return mapChatMessageDto(row);
    },
    async toggleChatReaction(cfg, waveId, messageId, emoji) {
        const row = await gql(cfg, WAVE_GRAPHQL.toggleChatReaction, { input: { waveId, messageId, emoji } }, "toggleWaveChatReaction");
        return mapChatMessageDto(row);
    },
    async markChatRead(cfg, input) {
        return gql(cfg, WAVE_GRAPHQL.markChatRead, { input }, "markWaveChatRead");
    },
    createTodo(cfg, input) {
        const todoId = input.todoId;
        const updateFields = {
            text: input.text,
            completed: input.completed ?? false,
            categories: input.categories,
            projectId: input.projectId ?? undefined,
            priority: input.priority,
            dueDate: input.dueDate ?? undefined,
        };
        return gql(cfg, WAVE_GRAPHQL.createTodo, {
            input: {
                todoId,
                text: input.text,
                categories: input.categories ?? [],
                projectId: input.projectId ?? null,
                priority: input.priority ?? "NONE",
                dueDate: input.dueDate ?? null,
                source: "LOCAL",
            },
        }, "createTodo")
            .then(async (created) => {
            if (!input.completed)
                return created;
            await WaveCloudAPI.updateTodo(cfg, todoId, updateFields);
            return created;
        })
            .catch(async (err) => {
            if (!isTodoCreateConflict(err))
                throw err;
            await WaveCloudAPI.updateTodo(cfg, todoId, updateFields);
            return { todoId };
        });
    },
    updateTodo(cfg, todoId, input) {
        return gql(cfg, WAVE_GRAPHQL.updateTodo, { id: todoId, input }, "updateTodo");
    },
};
function mapChatMessageDto(row) {
    const reactionCounts = Array.isArray(row.reactionCounts)
        ? row.reactionCounts
        : [];
    return {
        messageId: String(row.messageId ?? ""),
        waveId: String(row.waveId ?? ""),
        authorSub: String(row.authorSub ?? ""),
        authorKind: parseWaveChatAuthorKind(String(row.authorKind ?? "HUMAN")),
        authorName: row.authorName ?? null,
        body: String(row.body ?? ""),
        parentId: row.parentId ?? null,
        mentions: Array.isArray(row.mentions) ? row.mentions : [],
        status: parseWaveChatMessageStatus(String(row.status ?? "SENT")),
        createdAt: String(row.createdAt ?? ""),
        replyCount: Number(row.replyCount ?? 0),
        lastReplyAt: row.lastReplyAt ?? null,
        reactionCounts,
        viewerReactions: Array.isArray(row.viewerReactions)
            ? row.viewerReactions
            : [],
    };
}
/** Normalize GraphQL or subscription payload into a WaveChatMessage. */
export function parseWaveChatMessageDto(row) {
    return mapChatMessageDto(row);
}
